import { useContext } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  AlertBannerContext,
  API_RESPONSE_CODE,
  ALERT_BANNER_CODE,
  ALERT_BANNER_CODE_SEVERITY,
  BusinessType,
  type BusinessTypeCatalog,
  type BusinessTypeConfigGraphQLResponse
} from "@kivunova/kivufrontendcommon";
import { fetchGraphQL } from "../../Graphql/utils";
import { businessTypeConfigQuery } from "../../Graphql/querries/platformConfigQuery";

import {
  LOG_LEVEL,
  buildLogContext,
  captureAppException,
  sendAppLog
} from "../../Utils/appLogger";

/** Tenant enum catalog — Proxy resolves businessType from access token. */
export const useLoadBusinessTypeConfig = () => {
  const { setAlert } = useContext(AlertBannerContext);

  return useMutation<BusinessTypeCatalog, Error, void>({
    mutationKey: ["platformConfig", "businessTypeConfig"],
    mutationFn: async (): Promise<BusinessTypeCatalog> => {
      sendAppLog(
        LOG_LEVEL.INFO,
        "Started loading business type config",
        buildLogContext("platform-config", "load-business-type-config")
      );

      const catalogResponse = await fetchGraphQL<
        BusinessTypeConfigGraphQLResponse,
        Record<string, never>
      >(businessTypeConfigQuery, {}, setAlert);

      const catalog = catalogResponse.businessTypeConfig;
      if (catalog.code !== API_RESPONSE_CODE.OK) {
        const error = new Error(
          `Load business type config failed with code: ${catalog.code}`
        );
        captureAppException(
          "Load business type config failed",
          error,
          buildLogContext(
            "platform-config",
            "load-business-type-config",
            undefined,
            undefined,
            undefined,
            { api_code: catalog.code }
          )
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: catalog.code as unknown as ALERT_BANNER_CODE
        });
        throw error;
      }

      if (!catalog.businessType) {
        const error = new Error(
          "Load business type config missing businessType"
        );
        captureAppException(
          "Load business type config missing businessType",
          error,
          buildLogContext("platform-config", "load-business-type-config")
        );
        throw error;
      }

      const result: BusinessTypeCatalog = {
        businessType: catalog.businessType as BusinessType,
        sellingMode: catalog.sellingMode ?? [],
        baseUnit: catalog.baseUnit ?? []
      };

      sendAppLog(
        LOG_LEVEL.INFO,
        "Business type config loaded successfully",
        buildLogContext(
          "platform-config",
          "load-business-type-config",
          undefined,
          undefined,
          undefined,
          {
            business_type: result.businessType,
            selling_mode_count: result.sellingMode.length,
            base_unit_count: result.baseUnit.length
          }
        )
      );

      return result;
    }
  });
};
